/**
 * The typed client over the local search index's Tauri commands (PLAN
 * 2026-10-28_command_palette §12.2; Rust: src-tauri/src/palette_index).
 *
 *   search(args, { signal }) → Promise<{ hits, mode, truncated, backfilled, bodyBackfilled }>
 *   get(args, { signal })    → Promise<Hit[]>
 *   status()                 → Promise<{ available, backfilled, bodyBackfilled, rows, bodyRows,
 *                                         queueDepth, schemaVersion, lastRebuildAt }>
 *   rebuild({ part })        → Promise<{ started: true }>
 *   onProgress(fn) / onReady(fn) → unsubscribe
 *
 * Same API as the palette core's `testing/fakeIndexClient.js` and this
 * folder's `webIndexClient.js`, so a provider never knows which it has.
 *
 * - IPC cannot be cancelled. An already-aborted signal skips the call; a
 *   signal aborted while the call was in flight turns the answer into an
 *   `AbortError` (§12.1 rule 2), so a stale batch never reaches the UI.
 * - A failure rejects with an Error whose `message` and `code` are exactly
 *   one §12.2 code. Anything else (a Tauri transport error) becomes
 *   `index_unavailable`. The query is never put in an error or a log.
 */
import { abortError } from './abort';

export const INDEX_ERRORS = Object.freeze(['no_db', 'db_locked', 'index_unavailable', 'bad_args', 'query_too_long', 'busy']);
export const EVENT_PROGRESS = 'palette-index:progress';
export const EVENT_READY = 'palette-index:ready';

export function indexError(code) {
  const c = INDEX_ERRORS.includes(code) ? code : 'index_unavailable';
  const e = new Error(c);
  e.code = c;
  return e;
}

/**
 * @param {{ invoke: (cmd: string, args?: object) => Promise<any>,
 *           listen?: (event: string, fn: (e: {payload: any}) => void) => Promise<() => void> }} deps
 */
export function createIndexClient({ invoke, listen } = {}) {
  async function call(cmd, args, signal) {
    if (signal?.aborted) throw abortError();
    let value;
    try {
      value = await invoke(cmd, { args });
    } catch (err) {
      throw indexError(typeof err === 'string' ? err : err?.code || err?.message);
    }
    if (signal?.aborted) throw abortError();
    return value;
  }

  function subscribe(event, fn) {
    if (typeof listen !== 'function') return () => {};
    let unlisten = null;
    let gone = false;
    Promise.resolve(listen(event, (e) => fn(e?.payload)))
      .then((off) => {
        if (gone) off?.();
        else unlisten = off;
      })
      .catch(() => {});
    return () => {
      gone = true;
      unlisten?.();
    };
  }

  return {
    search: (args, { signal } = {}) => call('palette_search', args, signal),
    get: (args, { signal } = {}) => call('palette_get', args, signal),
    status: () => call('palette_index_status', {}),
    rebuild: ({ part } = {}) => call('palette_index_rebuild', { part }),
    onProgress: (fn) => subscribe(EVENT_PROGRESS, fn),
    onReady: (fn) => subscribe(EVENT_READY, fn),
  };
}

let desktopClient = null;

/** The desktop app's client (Tauri), created on first use. */
export async function desktopIndexClient() {
  if (desktopClient) return desktopClient;
  const [{ invoke }, { listen }] = await Promise.all([import('@tauri-apps/api/core'), import('@tauri-apps/api/event')]);
  desktopClient = createIndexClient({ invoke, listen });
  return desktopClient;
}
